import React, { FC, useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Icon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Divider,
  Card,
  CardContent,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  ListSubheader,
  SelectChangeEvent,
  FormHelperText,
} from "@mui/material";
import {
  RecipeFormData,
  IngredientOption,
  ExtendedBatch,
  Instruction,
} from "./types";
import { IngredientCard } from "./components/IngredientCard";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { InstructionEditor } from "./components/InstructionEditor";
import { UnitSelect } from "./components/UnitSelect";

// At the top of the file, add the type definition
type RecipeMode = "basic" | "ingredients" | "instructions";

export interface RecipeEditPanelProps {
  recipe: RecipeFormData;
  onChange: (recipe: RecipeFormData) => void;
  formErrors: {
    name: boolean;
    category: boolean;
  };
  mode: RecipeMode; // Use the type here
  onAddIngredient?: () => void;
  onEditIngredient?: (index: number) => void;
  ingredientOptions?: IngredientOption[];
  batchDataCache?: Record<string, any>;
  getBatchData?: (ingredientId: string) => Promise<any>;
}

export const RecipeEditPanel: React.FC<RecipeEditPanelProps> = ({
  recipe,
  onChange,
  formErrors,
  mode,
  onAddIngredient,
  onEditIngredient,
  ingredientOptions = [],
  batchDataCache = {},
  getBatchData = async () => null,
}) => {
  const [ingredientBatches, setIngredientBatches] = React.useState<{
    [key: string]: ExtendedBatch[];
  }>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = React.useState(false);

  const [loadingIngredients, setLoadingIngredients] = React.useState<
    Record<string, boolean>
  >({});

  // Load batch data for ingredients if not already cached
  React.useEffect(() => {
    let mounted = true;
    const loadMissingBatchData = async () => {
      const uniqueIngredientIds = new Set(
        recipe.ingredients.map((ing) => ing.ingredient_id)
      );

      for (const ingredientId of uniqueIngredientIds) {
        if (
          !batchDataCache[ingredientId] &&
          !loadingIngredients[ingredientId]
        ) {
          setLoadingIngredients((prev) => ({ ...prev, [ingredientId]: true }));
          await getBatchData(ingredientId);
          if (mounted) {
            setLoadingIngredients((prev) => ({
              ...prev,
              [ingredientId]: false,
            }));
          }
        }
      }
    };

    loadMissingBatchData();

    return () => {
      mounted = false;
    };
  }, [recipe.ingredients, batchDataCache, getBatchData, loadingIngredients]);

  const handleChange =
    (field: keyof RecipeFormData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      onChange({
        ...recipe,
        [field]: event.target.value,
      });
    };

  const handleUpdateInstructions = (instructions: Instruction[]) => {
    onChange({
      ...recipe,
      instructions: instructions.map((instruction) => ({
        content: instruction.content,
        preparation_time: instruction.preparation_time || 0,
        preparation_time_unit: "min",
        cooking_time: instruction.cooking_time || 0,
        cooking_time_unit: "min",
        temperature: instruction.temperature || 0,
        temperature_unit: instruction.temperature_unit || "C",
      })),
    });
  };

  const handleRemoveIngredient = (index: number) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients.splice(index, 1);
    onChange({
      ...recipe,
      ingredients: newIngredients,
    });
  };

  const handleDeleteClick = (index: number) => {
    setDeleteIndex(index);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteIndex !== null) {
      handleRemoveIngredient(deleteIndex);
    }
    setDeleteConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleClearAll = () => {
    onChange({
      ...recipe,
      ingredients: [],
    });
    setClearConfirmOpen(false);
  };

  const handleEditClick = (index: number) => {
    if (!onEditIngredient) return;
    onEditIngredient(index);
  };

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      {mode === "basic" && (
        <Box sx={{ mx: "auto" }}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                color: "text.primary",
                mb: 0.5,
              }}
            >
              Essential Information
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontFamily: "Montserrat",
              }}
            >
              Fill in the basic details about your recipe
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                label="Recipe Name"
                required
                value={recipe.name}
                onChange={handleChange("name")}
                error={formErrors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ color: "primary.main" }}>restaurant</Icon>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                    },
                    "&.Mui-focused": {
                      bgcolor: "background.paper",
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth required error={formErrors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={recipe.category}
                  label="Category"
                  onChange={handleChange("category")}
                  startAdornment={
                    <InputAdornment position="start">
                      <Icon sx={{ color: "primary.main" }}>category</Icon>
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                    },
                    "&.Mui-focused": {
                      bgcolor: "background.paper",
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                >
                  <MenuItem value="main_course">Main Course</MenuItem>
                  <MenuItem value="appetizer">Appetizer</MenuItem>
                  <MenuItem value="dessert">Dessert</MenuItem>
                  <MenuItem value="beverage">Beverage</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                label="Tags ( optional )"
                value={recipe.tags?.join(", ")}
                onChange={(e) =>
                  onChange({
                    ...recipe,
                    tags: e.target.value.split(",").map((tag) => tag.trim()),
                  })
                }
                placeholder="e.g., healthy, quick, vegetarian"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ color: "primary.main" }}>local_offer</Icon>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                    },
                    "&.Mui-focused": {
                      bgcolor: "background.paper",
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={12} lg={12} xl={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={recipe.description}
                onChange={handleChange("description")}
                placeholder="Describe your recipe, its origin, or any special notes"
                helperText="A brief description to introduce your recipe"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ color: "primary.main" }}>description</Icon>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                    },
                    "&.Mui-focused": {
                      bgcolor: "background.paper",
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                Yield & Servings
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontFamily: "Montserrat",
                  mb: 3,
                }}
              >
                Specify the output quantity and serving size
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Servings"
                value={recipe.servings}
                onChange={handleChange("servings")}
                helperText="Number of people the recipe serves"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ color: "primary.main" }}>people</Icon>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                    },
                    "&.Mui-focused": {
                      bgcolor: "background.paper",
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Output Quantity"
                required
                value={recipe.yield_quantity}
                onChange={handleChange("yield_quantity")}
                helperText="Total amount the recipe produces"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ color: "primary.main" }}>inventory</Icon>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                    },
                    "&.Mui-focused": {
                      bgcolor: "background.paper",
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Output Unit</InputLabel>
                <Select
                  value={recipe.yield_unit}
                  label="Output Unit"
                  onChange={handleChange("yield_unit")}
                  startAdornment={
                    <InputAdornment position="start">
                      <Icon sx={{ color: "primary.main" }}>straighten</Icon>
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: "background.paper",
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                    },
                    "&.Mui-focused": {
                      bgcolor: "background.paper",
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                >
                  <ListSubheader>Common Units</ListSubheader>
                  <MenuItem value="pcs">Pieces (pcs)</MenuItem>
                  <MenuItem value="pack">Pack</MenuItem>
                  <MenuItem value="box">Box</MenuItem>
                  <ListSubheader>Weight Units</ListSubheader>
                  <MenuItem value="kg">Kilogram (kg)</MenuItem>
                  <MenuItem value="g">Gram (g)</MenuItem>
                  <MenuItem value="oz">Ounce (oz)</MenuItem>
                  <MenuItem value="lb">Pound (lb)</MenuItem>
                  <ListSubheader>Volume Units</ListSubheader>
                  <MenuItem value="L">Liter (L)</MenuItem>
                  <MenuItem value="ml">Milliliter (ml)</MenuItem>
                  <MenuItem value="fl_oz">Fluid Ounce (fl oz)</MenuItem>
                  <MenuItem value="gal">Gallon (gal)</MenuItem>
                </Select>
                <FormHelperText>
                  Unit of measurement for the output
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}

      {mode === "ingredients" && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            flex: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "auto",
            border: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#0891B2",
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                }}
              >
                Ingredients
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {recipe.ingredients.length > 0 && (
                  <Button
                    startIcon={<Icon>delete_sweep</Icon>}
                    size="small"
                    sx={{
                      color: "#DC2626",
                      borderColor: "#DC2626",
                      "&:hover": {
                        borderColor: "#B91C1C",
                        bgcolor: alpha("#DC2626", 0.04),
                      },
                    }}
                    onClick={() => setClearConfirmOpen(true)}
                  >
                    Clear All
                  </Button>
                )}
                <Button
                  startIcon={<Icon>add</Icon>}
                  size="small"
                  variant="contained"
                  onClick={onAddIngredient}
                  sx={{
                    bgcolor: "#0891B2",
                    "&:hover": {
                      bgcolor: "#0E7490",
                    },
                  }}
                >
                  Add Ingredient
                </Button>
              </Box>
            </Box>

            {recipe.ingredients.length === 0 ? (
              <Card
                variant="outlined"
                sx={{
                  border: "1px solid",
                  borderColor: alpha("#0891B2", 0.1),
                }}
              >
                <CardContent>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Icon
                      sx={{ fontSize: 48, color: alpha("#0891B2", 0.3), mb: 2 }}
                    >
                      restaurant
                    </Icon>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontFamily: "Montserrat",
                        fontWeight: 600,
                        color: "#0891B2",
                      }}
                    >
                      No Ingredients Added
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontFamily: "Montserrat",
                      }}
                    >
                      Start adding ingredients to your recipe
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={1}>
                {recipe.ingredients.map((ingredient, index) => {
                  const ingredientDetails = ingredientOptions.find(
                    (opt) => opt.id === ingredient.item_id
                  );
                  const batches =
                    ingredientBatches[ingredient.ingredient_id] || [];
                  const selectedBatch = ingredient.selected_batch_id
                    ? batches.find(
                        (b) => b.batch_id === ingredient.selected_batch_id
                      )
                    : undefined;

                  return (
                    <IngredientCard
                      key={`${ingredient.item_id}-${index}`}
                      ingredient={ingredient}
                      ingredientDetails={ingredientDetails}
                      batchDetails={selectedBatch}
                      onEdit={() => handleEditClick(index)}
                      onDelete={() => handleDeleteClick(index)}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>
        </Paper>
      )}

      {mode === "instructions" && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2,
            minHeight: 300,
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          }}
        >
          <InstructionEditor
            instructions={recipe.instructions as any}
            onChange={handleUpdateInstructions}
          />
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 600,
          }}
        >
          Delete Ingredient
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="delete-dialog-description"
            sx={{
              fontFamily: "Montserrat",
            }}
          >
            Are you sure you want to remove this ingredient from the recipe?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: alpha("#0891B2", 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: "#DC2626",
              "&:hover": {
                bgcolor: "#B91C1C",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Confirmation Dialog */}
      <Dialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        aria-labelledby="clear-dialog-title"
        aria-describedby="clear-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle
          id="clear-dialog-title"
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 600,
          }}
        >
          Clear All Ingredients
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="clear-dialog-description"
            sx={{
              fontFamily: "Montserrat",
            }}
          >
            Are you sure you want to remove all ingredients from the recipe?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearConfirmOpen(false)}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: alpha("#0891B2", 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearAll}
            variant="contained"
            sx={{
              bgcolor: "#DC2626",
              "&:hover": {
                bgcolor: "#B91C1C",
              },
            }}
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
